import type {
	DeliveryClient,
	IContentItem,
	IContentItemsContainer,
} from '@kontent-ai/delivery-sdk'
import { client } from './client'
import { fnReturnData } from './fnReturnData'

/**
 * Kontent rejects a URL built from more than ~100 codenames with HTTP 414.
 * 80 keeps a safety margin for long codenames and a long `elements` parameter.
 */
const DEFAULT_CHUNK_SIZE = 80

/** How many chunk requests are in flight at once. */
const DEFAULT_CONCURRENCY = 6

export interface IGetItemsByCodenamesParams {
	codenames: string[]
	depth?: number
	elementsParameter?: string[]
	preview?: boolean
	chunkSize?: number
	concurrency?: number
	kontentClient?: DeliveryClient
}

export interface IGetItemsByCodenamesResult<T extends IContentItem> {
	items: T[]
	linkedItems: IContentItemsContainer
}

const chunk = <T>(list: T[], size: number): T[][] => {
	const chunks: T[][] = []
	for (let i = 0; i < list.length; i += size) {
		chunks.push(list.slice(i, i + size))
	}
	return chunks
}

export async function getItemsByCodenames<T extends IContentItem>({
	codenames,
	depth = 0,
	elementsParameter,
	preview,
	chunkSize = DEFAULT_CHUNK_SIZE,
	concurrency = DEFAULT_CONCURRENCY,
	kontentClient = client,
}: IGetItemsByCodenamesParams): Promise<IGetItemsByCodenamesResult<T>> {
	const uniqueCodenames = Array.from(new Set(codenames)).filter(Boolean)

	if (!uniqueCodenames.length) {
		return { items: [], linkedItems: {} }
	}

	const fetchChunk = async (codenameChunk: string[]) => {
		let query = kontentClient.items<T>().depthParameter(depth)

		if (elementsParameter) {
			query = query.elementsParameter(elementsParameter)
		}

		query = query
			.inFilter('system.codename', codenameChunk)
			.limitParameter(codenameChunk.length)

		try {
			return await query
				.queryConfig({ usePreviewMode: preview })
				.toPromise()
				.then(fnReturnData)
		} catch (error) {
			console.error(
				`[ERROR] getItemsByCodenames failed for ${codenameChunk.length} codenames (depth ${depth}):`,
				error,
			)
			throw new Error(
				`Failed to fetch content items by codename from Kontent (${codenameChunk.length} codenames, depth ${depth}). First codename: ${codenameChunk[0]}`,
			)
		}
	}

	const items: T[] = []
	const linkedItems: IContentItemsContainer = {}
	const seenItemCodenames = new Set<string>()
	const chunks = chunk(uniqueCodenames, chunkSize)

	for (const batch of chunk(chunks, concurrency)) {
		const responses = await Promise.all(batch.map(fetchChunk))

		responses.forEach((response) => {
			response.items.forEach((item) => {
				if (seenItemCodenames.has(item.system.codename)) return
				seenItemCodenames.add(item.system.codename)
				items.push(item)
			})
			Object.assign(linkedItems, response.linkedItems)
		})
	}

	return { items, linkedItems }
}
